import { useState } from 'react';
import type { Chiffrage } from '../types';
import { UNIVERS, STATUTS, ROLES } from '../types';
import { ActionModal } from './ActionModal';
import { deleteChiffrage, exportData } from '../services/api';
import './ChiffrageTable.css';

interface ChiffrageTableProps {
    chiffrages: Chiffrage[];
    loading: boolean;
    lastRefresh: Date;
    onRefresh: () => void;
    filterUnivers: string;
    setFilterUnivers: (v: string) => void;
    filterStatut: string;
    setFilterStatut: (v: string) => void;
    searchQuery: string;
    setSearchQuery: (v: string) => void;
}

export function ChiffrageTable({
    chiffrages,
    loading,
    lastRefresh,
    onRefresh,
    filterUnivers,
    setFilterUnivers,
    filterStatut,
    setFilterStatut,
    searchQuery,
    setSearchQuery,
}: ChiffrageTableProps) {
    const [modalChiffrage, setModalChiffrage] = useState<Chiffrage | null>(null);
    const [modalType, setModalType] = useState<'attribues' | 'partiel' | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    const openModal = (chiffrage: Chiffrage, type: 'attribues' | 'partiel') => {
        setModalChiffrage(chiffrage);
        setModalType(type);
    };

    const closeModal = () => {
        setModalChiffrage(null);
        setModalType(null);
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteChiffrage(id);
            setDeleteConfirm(null);
            onRefresh();
        } catch (err) {
            console.error('Erreur suppression:', err);
            alert('Erreur lors de la suppression');
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatutBadge = (statut: string) => {
        const classes: Record<string, string> = {
            'En cours': 'badge-pending',
            'Attribués': 'badge-success',
            'Partiel': 'badge-warning',
        };
        return `badge ${classes[statut] || ''}`;
    };

    return (
        <div className="table-container">
            <div className="table-header">
                <h2>Lignes de chiffrage</h2>
                <div className="table-actions">
                    <button className="btn-export" onClick={exportData}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Exporter
                    </button>
                    <button
                        className={`btn-refresh ${loading ? 'loading' : ''}`}
                        onClick={onRefresh}
                        disabled={loading}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M23 4v6h-6" />
                            <path d="M1 20v-6h6" />
                            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                        </svg>
                        Rafraîchir
                    </button>
                </div>
            </div>

            <div className="table-filters">
                <div className="filter-group">
                    <label>Univers</label>
                    <select value={filterUnivers} onChange={e => setFilterUnivers(e.target.value)}>
                        <option value="Tous">Tous</option>
                        {UNIVERS.map(u => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Statut</label>
                    <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
                        <option value="Tous">Tous</option>
                        {STATUTS.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group filter-search">
                    <label>Recherche</label>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Rechercher par sujet..."
                    />
                </div>

                <div className="filter-info">
                    <span>{chiffrages.length} résultat{chiffrages.length !== 1 ? 's' : ''}</span>
                    <span className="last-refresh">
                        Dernière MAJ: {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                </div>
            </div>

            {loading && chiffrages.length === 0 ? (
                <div className="table-loading">
                    <div className="spinner"></div>
                    <p>Chargement...</p>
                </div>
            ) : chiffrages.length === 0 ? (
                <div className="table-empty">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 17H5a2 2 0 00-2 2 2 2 0 002 2h14a2 2 0 002-2 2 2 0 00-2-2h-4" />
                        <path d="M12 17V1" />
                        <path d="M7 6l5-5 5 5" />
                    </svg>
                    <p>Aucune ligne de chiffrage</p>
                    <span>Créez votre première ligne avec le formulaire ci-dessus</span>
                </div>
            ) : (
                <div className="table-wrapper">
                    <table className="chiffrage-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Univers</th>
                                <th>Sujet</th>
                                <th>Tailles</th>
                                <th>Détail jours</th>
                                <th>Total</th>
                                <th>Statut</th>
                                <th>Orchestra</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chiffrages.map(c => (
                                <tr key={c.id} className={`status-${c.statut.toLowerCase().replace(' ', '-')}`}>
                                    <td className="cell-date">{formatDate(c.created_at)}</td>
                                    <td className="cell-univers">
                                        <span className={`univers-badge univers-${c.univers.toLowerCase()}`}>
                                            {c.univers}
                                        </span>
                                    </td>
                                    <td className="cell-sujet">{c.sujet}</td>
                                    <td className="cell-tailles">
                                        <div className="tailles-grid">
                                            {ROLES.map(({ key, label }) => (
                                                <span key={key} className="taille-item">
                                                    <abbr title={label}>{label.split(' ')[0]}</abbr>: {c[`taille_${key}` as keyof Chiffrage]}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="cell-detail">
                                        <div className="detail-grid">
                                            {ROLES.map(({ key, label }) => (
                                                <span key={key}>
                                                    <abbr title={label}>{label.split(' ')[0]}</abbr>: {c[`jours_${key}` as keyof Chiffrage]}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="cell-total">
                                        <strong>{c.total_jours}</strong>
                                        {c.jours_attribues !== null && c.jours_attribues !== c.total_jours && (
                                            <span className="attribues-info">
                                                ({c.jours_attribues} att.)
                                            </span>
                                        )}
                                    </td>
                                    <td className="cell-statut">
                                        <span className={getStatutBadge(c.statut)}>{c.statut}</span>
                                    </td>
                                    <td className="cell-orchestra">
                                        {c.numero_orchestra || '—'}
                                    </td>
                                    <td className="cell-actions">
                                        {c.statut === 'En cours' ? (
                                            <div className="action-buttons">
                                                <button
                                                    className="action-btn action-attribues"
                                                    onClick={() => openModal(c, 'attribues')}
                                                    title="Jours demandés attribués"
                                                >
                                                    ✓ Attribués
                                                </button>
                                                <button
                                                    className="action-btn action-partiel"
                                                    onClick={() => openModal(c, 'partiel')}
                                                    title="Jours partiellement attribués"
                                                >
                                                    ± Partiel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="action-buttons">
                                                <span className="action-done">Traité</span>
                                            </div>
                                        )}
                                        {deleteConfirm === c.id ? (
                                            <div className="delete-confirm">
                                                <span>Supprimer ?</span>
                                                <button onClick={() => handleDelete(c.id)}>Oui</button>
                                                <button onClick={() => setDeleteConfirm(null)}>Non</button>
                                            </div>
                                        ) : (
                                            <button
                                                className="action-btn action-delete"
                                                onClick={() => setDeleteConfirm(c.id)}
                                                title="Supprimer"
                                            >
                                                🗑️
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {modalChiffrage && modalType && (
                <ActionModal
                    chiffrage={modalChiffrage}
                    type={modalType}
                    onClose={closeModal}
                    onSuccess={onRefresh}
                />
            )}
        </div>
    );
}
